import React, { useEffect, useRef, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Building2, ImagePlus, Loader2, Pencil, Plus, RefreshCw, Trash2, X, Check } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { projectsApi, shipyardLogoUrl, type Shipyard } from '@/lib/projectsApi'
import { getShipyardImageSrc } from '@/lib/shipyards'
import { useTranslation } from 'react-i18next'

// mirrors the server-side sniff (PNG only, 1 MB cap) so an obviously wrong
// file is rejected before it crosses the wire. PNG rather than "any image"
// because the same logo is stamped into generated Word documents
const ACCEPTED_LOGO_TYPES = ['image/png']
const MAX_LOGO_BYTES = 1024 * 1024

function logoProblem(file: File): string | null {
  if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
    return 'Logo must be a PNG — it is also placed in generated Word documents, which need PNG bytes'
  }
  if (file.size > MAX_LOGO_BYTES) return `Logo must be under 1 MB (this one is ${(file.size / 1024 / 1024).toFixed(1)} MB)`
  return null
}

/** Uploaded logo wins; a yard shipped with the app falls back to its bundled asset. */
function logoSrcFor(s: Shipyard): { src?: string; builtIn: boolean } {
  const uploaded = shipyardLogoUrl(s)
  if (uploaded) return { src: uploaded, builtIn: false }
  const bundled = getShipyardImageSrc(s.name)
  return { src: bundled, builtIn: Boolean(bundled) }
}

export const ShipyardsSection: React.FC = () => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()

  const [rows, setRows] = useState<Shipyard[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  // add form
  const [newName, setNewName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [newLogo, setNewLogo] = useState<File | null>(null)
  const [newLogoPreview, setNewLogoPreview] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  // one hidden picker, retargeted per row
  const rowFileRef = useRef<HTMLInputElement>(null)
  const [logoTargetId, setLogoTargetId] = useState<number | null>(null)

  // inline rename / re-code
  const [editId, setEditId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editCode, setEditCode] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      setRows(await projectsApi.listShipyards({ includeInactive: true }))
    } catch (err: any) {
      showError(t('shipyards.loadFailed'), err?.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  // object URL for the not-yet-uploaded add-form logo
  useEffect(() => {
    if (!newLogo) { setNewLogoPreview(null); return }
    const url = URL.createObjectURL(newLogo)
    setNewLogoPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [newLogo])

  const replaceRow = (updated: Shipyard) =>
    setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)))

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (creating) return
    if (!newName.trim()) {
      showError(t('shipyards.nameRequired'))
      return
    }
    setCreating(true)
    try {
      const created = await projectsApi.createShipyard({
        name: newName.trim(),
        code: newCode.trim() || undefined,
      })
      // logo is a second call: the yard exists before it has an image, and a
      // failed upload must not lose the yard the user just named
      if (newLogo) {
        try {
          await projectsApi.uploadShipyardLogo(created.id, newLogo)
        } catch (err: any) {
          showError(t('shipyards.createdLogoFailed', { name: created.name }), err?.message)
        }
      }
      success(t('shipyards.added', { name: created.name }))
      setNewName(''); setNewCode(''); setNewLogo(null)
      await load()
    } catch (err: any) {
      showError(t('shipyards.addFailed'), err?.message)
    } finally {
      setCreating(false)
    }
  }

  const pickLogoFor = (id: number) => {
    setLogoTargetId(id)
    rowFileRef.current?.click()
  }

  const handleRowLogoPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''            // same file twice in a row still fires change
    const id = logoTargetId
    setLogoTargetId(null)
    if (!file || id === null) return

    const problem = logoProblem(file)
    if (problem) { showError(problem.startsWith('Logo must be under') ? t('shipyards.logoTooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }) : t('shipyards.logoPngOnly')); return }

    setBusyId(id)
    try {
      replaceRow(await projectsApi.uploadShipyardLogo(id, file))
      success(t('shipyards.logoUpdated'))
    } catch (err: any) {
      showError(t('shipyards.logoUploadFailed'), err?.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleRemoveLogo = async (s: Shipyard) => {
    setBusyId(s.id)
    try {
      replaceRow(await projectsApi.deleteShipyardLogo(s.id))
      success(t('shipyards.logoRemoved', { name: s.name }))
    } catch (err: any) {
      showError(t('shipyards.logoRemoveFailed'), err?.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleToggleActive = async (s: Shipyard) => {
    setBusyId(s.id)
    try {
      replaceRow(await projectsApi.updateShipyard(s.id, { is_active: !s.is_active }))
      success(s.is_active ? t('shipyards.hidden', { name: s.name }) : t('shipyards.activated', { name: s.name }))
    } catch (err: any) {
      showError(t('shipyards.updateFailed'), err?.message)
    } finally {
      setBusyId(null)
    }
  }

  const startEdit = (s: Shipyard) => {
    setEditId(s.id); setEditName(s.name); setEditCode(s.code)
  }

  const handleSaveEdit = async (s: Shipyard) => {
    if (!editName.trim() || !editCode.trim()) {
      showError(t('shipyards.nameCodeRequired'))
      return
    }
    setBusyId(s.id)
    try {
      replaceRow(await projectsApi.updateShipyard(s.id, { name: editName.trim(), code: editCode.trim() }))
      setEditId(null)
      success(t('shipyards.updated'))
    } catch (err: any) {
      showError(t('shipyards.updateFailed'), err?.message)
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-6">
      <input
        ref={rowFileRef}
        type="file"
        accept={ACCEPTED_LOGO_TYPES.join(',')}
        className="hidden"
        onChange={handleRowLogoPicked}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            {t('shipyards.addTitle')}
          </CardTitle>
          <CardDescription>
            {t('shipyards.addDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                  {t('shipyards.name')}
                </label>
                <Input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Hyundai Mipo Dockyard"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
                  {t('shipyards.initials')} <span className="font-normal text-slate-400">({t('common.optional')})</span>
                </label>
                <Input
                  value={newCode}
                  onChange={e => setNewCode(e.target.value)}
                  maxLength={8}
                  placeholder={t('shipyards.derivedFromName')}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
                <ImagePlus className="h-4 w-4" />
                {newLogo ? t('shipyards.changeLogo') : t('shipyards.chooseLogo')}
                <input
                  type="file"
                  accept={ACCEPTED_LOGO_TYPES.join(',')}
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0]
                    e.target.value = ''
                    if (!file) return
                    const problem = logoProblem(file)
                    if (problem) { showError(problem.startsWith('Logo must be under') ? t('shipyards.logoTooLarge', { size: (file.size / 1024 / 1024).toFixed(1) }) : t('shipyards.logoPngOnly')); return }
                    setNewLogo(file)
                  }}
                />
              </label>

              {newLogoPreview && (
                <span className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900">
                  <img src={newLogoPreview} alt="" className="max-h-8 max-w-[140px] object-contain" />
                  <button
                    type="button"
                    onClick={() => setNewLogo(null)}
                    className="text-slate-400 hover:text-rose-600"
                    title={t('shipyards.removeSelectedLogo')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              )}

              <span className="text-xs text-slate-400">{t('shipyards.logoHelp')}</span>

              <Button type="submit" loading={creating} className="ml-auto">
                {t('shipyards.add')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {t('shipyards.title')}
              </CardTitle>
              <CardDescription>
                {t('shipyards.totalDescription', { count: rows.length })}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => void load()} title={t('shipyards.reload')}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('shipyards.loading')}
            </div>
          ) : rows.length === 0 ? (
            <p className="py-6 text-sm text-slate-500">{t('shipyards.empty')}</p>
          ) : (
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {rows.map(s => {
                const { src, builtIn } = logoSrcFor(s)
                const isBusy = busyId === s.id
                const isEditing = editId === s.id
                return (
                  <li key={s.id} className="flex flex-wrap items-center gap-3 py-3">
                    <span className="flex h-11 w-24 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-900">
                      {src
                        ? <img src={src} alt="" className="max-h-9 max-w-full object-contain" />
                        : <Building2 className="h-5 w-5 text-slate-300" />}
                    </span>

                    {isEditing ? (
                      <div className="flex min-w-[240px] flex-1 flex-wrap items-center gap-2">
                        <Input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="min-w-[180px] flex-1"
                        />
                        <Input
                          value={editCode}
                          onChange={e => setEditCode(e.target.value)}
                          maxLength={8}
                          className="w-28"
                        />
                        <Button size="sm" onClick={() => void handleSaveEdit(s)} loading={isBusy}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="min-w-[200px] flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {s.name}
                          {s.is_active === false && (
                            <span className="ml-2 rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300">
                              {t('shipyards.inactive')}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {s.code}
                          {builtIn && <span className="ml-2 text-slate-400">{t('shipyards.builtInLogo')}</span>}
                        </p>
                      </div>
                    )}

                    {!isEditing && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => pickLogoFor(s.id)}
                          loading={isBusy}
                          title={s.has_logo ? t('shipyards.replaceLogo') : t('shipyards.uploadLogo')}
                        >
                          <ImagePlus className="h-4 w-4" />
                          <span className="hidden sm:inline">{s.has_logo ? t('shipyards.replace') : t('shipyards.logo')}</span>
                        </Button>
                        {s.has_logo && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleRemoveLogo(s)}
                            disabled={isBusy}
                            title={t('shipyards.removeUploadedLogo')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => startEdit(s)} title={t('shipyards.renameTitle')}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void handleToggleActive(s)}
                          disabled={isBusy}
                          className="text-xs"
                        >
                          {s.is_active === false ? t('shipyards.activate') : t('shipyards.deactivate')}
                        </Button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
